// Shared display helpers for the gift card redemption admin screens.

// Fired with `detail: { count }` when a fresh actionNeededCount is known, or with no
// count when a review changed it and the sidebar should re-fetch.
export const ACTION_NEEDED_COUNT_EVENT = 'gift-card-action-needed-count';

export function emitActionNeededCount(count) {
  window.dispatchEvent(new CustomEvent(ACTION_NEEDED_COUNT_EVENT, { detail: { count } }));
}

// Admins only see these four states; the raw backend `status` is debug-only.
const ADMIN_STATUS_DISPLAY = {
  action_needed: { label: 'Action needed', className: 'needs-review' },
  sent: { label: 'Sent', className: 'success' },
  refunded: { label: 'Refunded', className: 'failed' },
  in_progress: { label: 'Placing order', className: 'gc-pending' },
};

export function getStatusDisplay(redemption) {
  const key = String(redemption?.adminStatus || '').toLowerCase();
  const display = ADMIN_STATUS_DISPLAY[key] || { label: '—', className: 'gc-pending' };
  return { ...display, label: redemption?.adminStatusLabel || display.label };
}

// Returns null when the environment wasn't tracked (older orders).
export function getEnvDisplay(environment) {
  const key = String(environment || '').toLowerCase();
  if (key === 'live') return { label: 'LIVE', className: 'gc-env-live' };
  if (key === 'test') return { label: 'TEST', className: 'gc-env-test' };
  if (key === 'unknown') return { label: 'UNKNOWN', className: 'gc-env-test' };
  return null;
}

export function formatNumber(value) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(Number(value));
}

export function formatUsd(amount) {
  if (amount == null || amount === '' || Number.isNaN(Number(amount))) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(amount));
}

// Face value in the card's own currency, e.g. "1,000 INR" — never a "$" prefix.
export function formatDenomination(amount, currencyCode) {
  const value = formatNumber(amount);
  if (value === '—') return value;
  return currencyCode ? `${value} ${currencyCode}` : value;
}

// problem.cardOrdered → whether the admin can safely reject without checking Xoxoday.
const CARD_ORDERED_DISPLAY = {
  no: { label: 'Gift card was NOT bought — safe to reject', short: 'Not bought', className: 'success' },
  unknown: { label: 'Not sure if bought — check Xoxoday first', short: 'Check Xoxoday', className: 'pending' },
};

export function getCardOrderedDisplay(cardOrdered) {
  return CARD_ORDERED_DISPLAY[String(cardOrdered || '').toLowerCase()] || null;
}

// --- Xoxoday provider settings ---

export const XOXODAY_DEFAULT_HOSTS = {
  test: 'canvas.xoxoday.com',
  live: 'stores.xoxoday.com',
};

const CREDENTIAL_SOURCE_LABELS = {
  admin: 'Set in admin',
  '.env': 'From server .env',
  none: 'Not set',
};

export function getCredentialSourceLabel(source) {
  if (!source) return CREDENTIAL_SOURCE_LABELS.none;
  return CREDENTIAL_SOURCE_LABELS[source] || source;
}

const RELATIVE_UNITS = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

// "in 3 days", "2 hours ago", "just now" — in the admin's local time.
export function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';

  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  for (const [unit, seconds] of RELATIVE_UNITS) {
    if (Math.abs(diffSeconds) >= seconds) return rtf.format(Math.round(diffSeconds / seconds), unit);
  }
  return 'just now';
}

export function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  const day = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${day} · ${time}`;
}

export const XOXODAY_SETTINGS_PATH = '/gift-card-provider-settings';

export function getEnvLabel(environment) {
  return String(environment || '').toUpperCase();
}

// "expires in 3 days" / "expired" / "no expiry recorded"; `expired` drives the red style.
export function getExpiryDisplay(expiresAt) {
  if (!expiresAt) return { text: 'no expiry recorded', expired: false };
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return { text: 'no expiry recorded', expired: false };
  if (date.getTime() <= Date.now()) return { text: `expired ${formatRelativeTime(expiresAt)}`, expired: true };
  return { text: `expires ${formatRelativeTime(expiresAt)}`, expired: false };
}
